import CorporateFirstLoginRepository from "core/authUser/auth/repositories/CorporateFirstLoginRepository";
import {
  CorporateCodeRequestOutcome,
  CorporateCodeVerificationOutcome,
  CorporatePasswordOutcome,
  SetCorporatePasswordInput,
} from "core/authUser/auth/entities/CorporateFirstLogin";
import { RepositoryResult, RepositoryResultFactory } from "core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { toKybException } from "../../http/services/kybRequest/kybRequest";

const BASE = "/auth/corporate";

function httpStatusOf(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }
  const { status, code } = error as { status?: unknown; code?: unknown };
  if (typeof status === "number") {
    return status;
  }
  return typeof code === "string" && /^\d{3}$/.test(code) ? Number(code) : undefined;
}

export default class CorporateFirstLoginRepositoryBackend implements CorporateFirstLoginRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async requestCode(email: string): Promise<RepositoryResult<CorporateCodeRequestOutcome>> {
    try {
      await this.httpClient.post<ApiResponse<null>, { email: string }>({
        url: `${BASE}/forgot-password`,
        body: { email },
        withAuth: false,
      });
      return RepositoryResultFactory.success("sent");
    } catch (error) {
      if (httpStatusOf(error) === 429) {
        return RepositoryResultFactory.success("rateLimited");
      }
      return RepositoryResultFactory.error(toKybException(error));
    }
  }

  async verifyCode(email: string, otp: string): Promise<RepositoryResult<CorporateCodeVerificationOutcome>> {
    try {
      const response = await this.httpClient.post<ApiResponse<{ resetToken: string }>, { email: string; otp: string }>({
        url: `${BASE}/verify-otp`,
        body: { email, otp },
        withAuth: false,
      });
      return RepositoryResultFactory.success({ outcome: "verified", resetToken: response.data.resetToken });
    } catch (error) {
      const status = httpStatusOf(error);
      if (status === 429) {
        return RepositoryResultFactory.success({ outcome: "locked" });
      }
      if (status === 400) {
        return RepositoryResultFactory.success({ outcome: "invalid" });
      }
      return RepositoryResultFactory.error(toKybException(error));
    }
  }

  async setPassword(input: SetCorporatePasswordInput): Promise<RepositoryResult<CorporatePasswordOutcome>> {
    try {
      await this.httpClient.post<ApiResponse<null>, SetCorporatePasswordInput>({
        url: `${BASE}/reset-password`,
        body: input,
        withAuth: false,
      });
      return RepositoryResultFactory.success("done");
    } catch (error) {
      const status = httpStatusOf(error);
      if (status === 400) {
        return RepositoryResultFactory.success("policy");
      }
      if (status === 401) {
        return RepositoryResultFactory.success("tokenInvalid");
      }
      return RepositoryResultFactory.error(toKybException(error));
    }
  }
}
